import React, { useEffect, useState } from 'react';
import { useNotify, useRefresh } from 'react-admin';
import { RRule } from 'rrule';
import moment from 'moment';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import { useMediaQuery } from '@material-ui/core';
import { apiGet, apiPost } from '../brainApi';
import dataProvider from '../dataProvider';
import RecurringPlanCustomerDetails from './RecurringPlanCustomerDetails';
import RecurringPlanCalendar from './RecurringPlanCalendar';
import RecurringPlanForm from './RecurringPlanForm';
import RecurringPlanSummary from './RecurringPlanSummary';
import RecurringPlanCostPreview from './RecurringPlanCostPreview';
import RecurringPlanString from './RecurringPlanString';
import DialogTitle from '../components/DialogTitle';
import { areTimeSlotsEnabled, isEmpty } from '../helper';
import { allTimes } from '../QuickActions/ScheduleForm';

const useStyles = makeStyles(() => ({
  gridBase: {
    display: 'flex',
    alignItems: 'center',
  },
  recurringPlanDetails: {
    paddingLeft: '15px',
    backgroundColor: '#f2f2f2',
    maxWidth: '300px',
  },
  negativeMarginTop: {
    marginTop: '-20px',
  },
  actionButtons: {
    justifyContent: 'center',
  },
  divide: {
    margin: '5px 0 15px 0',
    backgroundColor: '#000',
  },
}));

export const endDate = (val = null) => {
  const d = val ? new Date(val) : new Date();
  const dateStr = new Date(d.setDate(d.getDate() + 11 * 7))
    .toISOString()
    .slice(0, 10);
  return new Date(`${dateStr}T23:59:59.000+00:00`);
};

const { WEEKLY, MONTHLY, MO, TU, WE, TH, FR, SA, SU } = RRule;

const daysOfWeekMap = [
  { rruleValue: SU, text: 'Sunday' },
  { rruleValue: MO, text: 'Monday' },
  { rruleValue: TU, text: 'Tuesday' },
  { rruleValue: WE, text: 'Wednesday' },
  { rruleValue: TH, text: 'Thursday' },
  { rruleValue: FR, text: 'Friday' },
  { rruleValue: SA, text: 'Saturday' },
];

export const handleRruleChange = (attribute, value, rruleAttributes) => {
  if (
    ['dtstart', 'until'].includes(attribute) &&
    new Date(value).toDateString() === 'Invalid Date'
  ) {
    // If a user is in the middle of typing a date, do not make a change if the date is invalid to avoid errors
    return [false, rruleAttributes];
  }
  const newAttributeObj = {
    ...rruleAttributes,
    [attribute]: value,
  };
  if (attribute === 'freq') {
    newAttributeObj.freq = value === 'monthly' ? MONTHLY : WEEKLY;
    newAttributeObj.interval = value === 'biweekly' ? 2 : 1;
  }
  if (attribute === 'dtstart') {
    newAttributeObj.until = endDate(value);
  }
  const start = newAttributeObj.dtstart;
  // End date cannot be before start date and limit time range to a max of 1 year
  const isEndDateInvalid =
    start > newAttributeObj.until ||
    moment(start).add(1, 'years') < newAttributeObj.until;
  const weekdayValue = daysOfWeekMap[start.getDay()].rruleValue;
  // Calculate week number for monthly pattern (ex: 4th Monday)
  const weekNumber = Math.ceil(start.getDate() / 7);
  newAttributeObj.byweekday =
    newAttributeObj.freq === MONTHLY
      ? weekdayValue.nth(weekNumber)
      : weekdayValue;
  return [isEndDateInvalid, newAttributeObj];
};

const isUsingTimeSlots = areTimeSlotsEnabled();

export const getTimeSlotOptions = (serviceVehicleDates, rruleAttributes) => {
  // If Pro is using time slots, find available start times of start date
  if (isUsingTimeSlots && serviceVehicleDates?.length > 0) {
    const startDate = serviceVehicleDates.find(
      (d) => d.date === moment(rruleAttributes.dtstart).format('YYYY-MM-DD'),
    );
    if (startDate?.available_start_times?.length > 0) {
      return startDate.available_start_times.map((t) =>
        moment(t).format('h:mm a'),
      );
    }
  }
  return null;
};

const RecurringPlanModal = ({
  customer,
  vehicle,
  sw,
  showRecurringModal,
  setShowRecurringModal,
}) => {
  const {
    scheduled_work_id,
    customer_address,
    client_location,
    service_vehicle_id,
    admin_note,
  } = sw || {};
  const { customer_address_id, address_line_1 } = customer_address || {};
  const { client_location_id } = client_location || {};
  const classes = useStyles();
  const notify = useNotify();
  const refresh = useRefresh();

  // Frequency for weekly and biweekly will be WEEKLY
  // Biweekly patterns will have an interval of 2
  // Byweekday will match the dtstart value - we will calculate the weekday from the date and use the index of the dayMap to get the weekday
  // Byhour sets the UTC time to be noon - this ensures that all American time zones will always be on the same weekday in all occurrences
  const [rruleAttributes, setRruleAttributes] = useState({
    dtstart: new Date(),
    until: endDate(),
    freq: WEEKLY,
    byweekday: daysOfWeekMap[new Date().getDay()].rruleValue,
    interval: 1,
    byhour: 12,
  });
  const [rruleCombined, setRruleCombined] = useState(
    new RRule(rruleAttributes),
  );
  const [datesToCreate, setDatesToCreate] = useState(rruleCombined.all());
  const [
    availableDateMapForAllServiceVehicles,
    setAvailableDateMapForAllServiceVehicles,
  ] = useState({});
  const [serviceVehicleDates, setServiceVehicleDates] = useState(null);
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [datesWithConflicts, setDatesWithConflicts] = useState([]);
  const [allServiceVehicles, setAllServiceVehicles] = useState([]);
  const [selectedServiceVehicleId, setSelectedServiceVehicleId] = useState(
    service_vehicle_id || null,
  );
  const [isScheduleLoading, setIsScheduleLoading] = useState(false);
  const [isEndDateInvalid, setIsEndDateInvalid] = useState(false);
  const [isLoadingAdditionalDates, setIsLoadingAdditionalDates] = useState(
    false,
  );
  const [timeSlotOptions, setTimeSlotOptions] = useState([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('');
  const weekLimit = 6;

  useEffect(() => {
    dataProvider
      .getList('service-vehicle', {
        filter: {
          is_active: true,
          is_service_bay: sw?.location_type === 'shop',
        },
        sort: { field: 'name', order: 'ASC' },
        pagination: { page: 1, perPage: 2000 },
      })
      .then(({ data }) => {
        setAllServiceVehicles(data);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  useEffect(() => {
    setSelectedServiceVehicleId(service_vehicle_id);
  }, [service_vehicle_id]);

  useEffect(() => {
    if (selectedServiceVehicleId && allServiceVehicles.length > 0) {
      setSelectedServiceVehicleId(
        allServiceVehicles.find(
          (sv) => sv.service_vehicle_id === selectedServiceVehicleId,
        )?.service_vehicle_id,
      );
    }
  }, [allServiceVehicles, selectedServiceVehicleId]);

  const onRruleChange = (attribute, value) => {
    const [endDateInvalid, rruleAttributeObj] = handleRruleChange(
      attribute,
      value,
      rruleAttributes,
    );
    setIsEndDateInvalid(endDateInvalid);
    setRruleAttributes(rruleAttributeObj);
    setRruleCombined(new RRule(rruleAttributeObj));
  };

  const filterAvailableDates = (dateMap) => {
    return Object.keys(dateMap)
      .map((d) => {
        if (dateMap[d]?.length > 0) {
          const selectedSchedule = dateMap[d].find(
            (s) => s.service_vehicle_id === selectedServiceVehicleId,
          );
          if (selectedSchedule) {
            return { ...selectedSchedule, date: d };
          }
        }
      })
      .filter((d) => d);
  };

  const selectStartDate = (dates) => {
    if (
      dates.length > 0 &&
      !dates
        .map((d) => d.date)
        .includes(moment(rruleAttributes.dtstart).format('YYYY-MM-DD'))
    ) {
      onRruleChange('dtstart', new Date(`${dates[0].date}T00:00:00`));
    }
  };

  const loadAvailableDates = (service_offering_ids, totalLaborHours) => {
    const params = {
      use_new_version: true,
      requested_service_offering_ids: service_offering_ids,
      total_labor_hours: totalLaborHours,
    };
    if (address_line_1) {
      params.home_address_id = customer_address_id;
    } else {
      params.client_location_id = client_location_id;
    }

    setIsScheduleLoading(true);
    apiGet('/get-compatible-dates', { ...params, week_limit: weekLimit }).then(
      (dateMap) => {
        setAvailableDateMapForAllServiceVehicles(dateMap);
        const filteredAvailableDates = filterAvailableDates(dateMap);
        selectStartDate(filteredAvailableDates);
        setIsScheduleLoading(false);
        // For at-home services, load again without week limit for all dates
        if (!client_location_id) {
          setIsLoadingAdditionalDates(true);
          apiGet('/get-compatible-dates', params).then((dateMap) => {
            setAvailableDateMapForAllServiceVehicles(dateMap);
            setIsLoadingAdditionalDates(false);
          });
        } else {
          setIsLoadingAdditionalDates(false);
        }
      },
    );
  };

  useEffect(() => {
    if (!isEmpty(availableDateMapForAllServiceVehicles)) {
      const filteredAvailableDates = filterAvailableDates(
        availableDateMapForAllServiceVehicles,
      );
      setServiceVehicleDates(filteredAvailableDates);
    }
  }, [availableDateMapForAllServiceVehicles]);

  useEffect(() => {
    if (selectedServiceVehicleId) {
      if (!serviceVehicleDates) {
        const service_offering_ids =
          sw?.services.map((s) => s.service_offering_id) || [];
        const totalLaborHours =
          sw?.services?.reduce((total, service) => {
            return total + service.labor_hours;
          }, 0) || 0;
        if (service_offering_ids?.length > 0) {
          // Use the new version of compatible dates to load all schedule days and their details
          loadAvailableDates(service_offering_ids, totalLaborHours);
        }
      } else {
        const filteredAvailableDates = filterAvailableDates(
          availableDateMapForAllServiceVehicles,
        );
        setServiceVehicleDates(filteredAvailableDates);
        selectStartDate(filteredAvailableDates);
      }
    }
  }, [selectedServiceVehicleId]);

  useEffect(() => {
    const rruleDates = rruleCombined
      .all()
      .map((d) => moment(d).format('YYYY-MM-DD'));
    const serviceVehicleDateSet = new Set(
      serviceVehicleDates?.map((d) => d.date),
    );
    const availableDatesInPattern = rruleDates
      .filter((date) => serviceVehicleDateSet.has(date))
      .map((d) => new Date(`${d}T00:00:00`));
    const unavailableDatesInPattern = rruleDates
      .filter((date) => {
        return isLoadingAdditionalDates
          ? !serviceVehicleDateSet.has(date) &&
              date <=
                moment()
                  .add(weekLimit, 'weeks')
                  .format('YYYY-MM-DD')
          : !serviceVehicleDateSet.has(date);
      })
      .map((d) => new Date(`${d}T00:00:00`));

    setDatesToCreate(availableDatesInPattern);
    setUnavailableDates(unavailableDatesInPattern);
    const datesWithConflictsInPattern = rruleDates
      .map((date) => {
        const matchingDate = serviceVehicleDates?.find((d) => d.date === date);
        let hasDateConflict = matchingDate && !matchingDate.is_available;
        if (isLoadingAdditionalDates) {
          // If we have only loaded weekLimit weeks of compatible dates, only include dates within that range
          hasDateConflict =
            hasDateConflict &&
            date <=
              moment()
                .add(weekLimit, 'weeks')
                .format('YYYY-MM-DD');
        }
        if (!hasDateConflict && isUsingTimeSlots && selectedTimeSlot) {
          const hasTimeConflict = !matchingDate?.available_start_times
            .map((t) => moment(t).format('h:mm a'))
            .includes(selectedTimeSlot);
          if (hasTimeConflict) {
            return {
              date,
              short_reason: `${selectedTimeSlot} Time Not Free`,
            };
          }
        }
        if (hasDateConflict) {
          return {
            date,
            short_reason: matchingDate.unavailable_reason_short,
          };
        }
      })
      .filter((d) => d);
    setDatesWithConflicts(datesWithConflictsInPattern);
  }, [
    rruleCombined,
    serviceVehicleDates,
    isLoadingAdditionalDates,
    selectedTimeSlot,
  ]);

  useEffect(() => {
    const availableSlots = getTimeSlotOptions(
      serviceVehicleDates,
      rruleAttributes,
    );
    if (availableSlots) {
      setTimeSlotOptions(availableSlots);
      setSelectedTimeSlot(availableSlots[0]);
    } else {
      setTimeSlotOptions([]);
      setSelectedTimeSlot(allTimes()[0]);
    }
  }, [serviceVehicleDates, rruleAttributes]);

  const handleSubmit = () => {
    const dateStrings = datesToCreate.map((date) =>
      moment(date).format('YYYY-MM-DD'),
    );
    // The rruleCombined split will return "every week", "every 2 weeks", or "every month"
    const recurringPatternString = rruleCombined.toText().split(' on ')[0];
    const recurringNote = `Repeat Appointments (${recurringPatternString}) end on ${moment(
      rruleAttributes.until,
    ).format('MMMM DD')}.`;
    let payload = {
      existing_scheduled_work_id: scheduled_work_id,
      dates: dateStrings,
      service_vehicle_id: selectedServiceVehicleId,
      admin_note: `${recurringNote} ${admin_note || ''}`,
      recurring_pattern_string: recurringPatternString,
    };
    if (isUsingTimeSlots) {
      payload = {
        ...payload,
        confirmed_appointment_start_time: moment(
          selectedTimeSlot,
          'h:mm a',
        ).format('HH:mm:ss'),
      };
    }
    notify(
      'We are creating your repeat appointments. This may take up to a few minutes. We will notify you (see the bell icon) when they are ready.',
    );
    setShowRecurringModal(false);
    apiPost('/bulk-scheduled-work', payload)
      .then(() => {
        notify(`${dateStrings.length} Repeat Appointments have been created`);
        refresh();
      })
      .catch(() => {
        notify(
          'There was an error creating repeat appointments. Please reach out to your Zippity representative.',
        );
      });
  };

  const isLarge = useMediaQuery((theme) => theme.breakpoints.up('lg'));

  return (
    <Dialog
      open={showRecurringModal}
      onClose={() => setShowRecurringModal(false)}
    >
      <DialogTitle onClose={() => setShowRecurringModal(false)}>
        Create Repeat Appointments
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Grid container xs={12} className={classes.gridBase}>
          <Grid item xs={12}>
            <Typography variant="subtitle2">Details</Typography>
            <RecurringPlanCustomerDetails
              customer={customer}
              vehicle={vehicle}
              sw={sw}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2">Services</Typography>
            <RecurringPlanCostPreview sw={sw} />
          </Grid>
          {isScheduleLoading ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <Grid item xs={12}>
                <Divider className={classes.divide} />
              </Grid>
              <Grid item xs={12}>
                <Grid container className={classes.gridBase}>
                  <Grid item xs={12}>
                    <RecurringPlanString
                      rruleCombined={rruleCombined}
                      selectedTimeSlot={selectedTimeSlot}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={5}
                    className={`${classes.recurringPlanDetails} ${isLarge &&
                      classes.negativeMarginTop}`}
                  >
                    <RecurringPlanForm
                      rruleAttributes={rruleAttributes}
                      rruleCombined={rruleCombined}
                      onRruleChange={onRruleChange}
                      isEndDateInvalid={isEndDateInvalid}
                      selectedServiceVehicleId={selectedServiceVehicleId}
                      setSelectedServiceVehicleId={setSelectedServiceVehicleId}
                      allServiceVehicles={allServiceVehicles}
                      timeSlotOptions={timeSlotOptions}
                      selectedTimeSlot={selectedTimeSlot}
                      setSelectedTimeSlot={setSelectedTimeSlot}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RecurringPlanCalendar
                      rruleAttributes={rruleAttributes}
                      onRruleChange={onRruleChange}
                      serviceVehicleDates={serviceVehicleDates}
                      datesToCreate={datesToCreate}
                      datesWithConflicts={datesWithConflicts}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <RecurringPlanSummary
                rruleAttributes={rruleAttributes}
                datesToCreate={datesToCreate}
                setDatesToCreate={setDatesToCreate}
                unavailableDates={unavailableDates}
                datesWithConflicts={datesWithConflicts}
                showConflictLoadingIndicator={isLoadingAdditionalDates}
              />
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions className={classes.actionButtons}>
        <Button
          onClick={() => {
            setShowRecurringModal(false);
          }}
          color="primary"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={
            isEndDateInvalid ||
            datesToCreate.length === 0 ||
            (isUsingTimeSlots && !selectedTimeSlot)
          }
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecurringPlanModal;
